<template>
<div>
	<b-navbar class="submenu">
		<b-container>
			<b-navbar-brand href="#">Questions Administration</b-navbar-brand>
			<b-collapse id="nav-collapse-sub" is-nav>
				<b-navbar-nav>
					<a href="#" @click="loadQuestionnaireTab()" :class="{ 'active': tabIndex === 0 }">Questionnaires</a>
					<a href="#" @click="loadQuestionTab()" :class="{ 'active': tabIndex === 1 }">Questions</a>
					<a href="#" @click="onLoad()" v-if="questionnaire">Reload</a>
				</b-navbar-nav>
				<b-navbar-nav class="ml-auto">
					<b-button 
						variant="secondary" 
						size="sm" 
						@click="navigateToV1"
						class="mr-2"
					>
						<i class="fa fa-arrow-left mr-1"></i>
						Back to V1
					</b-button>
				</b-navbar-nav>
			</b-collapse>
		</b-container>
	</b-navbar>

	<b-alert
		v-model="showAlert"
		:variant="alertVariant"
		dismissible
		class="alert-fixed"
	>
		{{ alertMessage }}
	</b-alert>

	<div id="content">
		<div v-if="isLoading" class="loading-state">
			<b-spinner variant="primary" type="grow"></b-spinner>
			<div class="mt-2">Loading questions...</div>
		</div>

		<b-alert
			v-if="error"
			show
			variant="danger"
			dismissible
		>
			{{ error }}
		</b-alert>

		<!-- Questionnaires Tab -->
		<div v-if="!isLoading && !error && tabIndex === 0" class="row">
			<!-- Left panel with questionnaire list -->
			<div class="col-md-3 mb-4" v-if="questionnaires">
				<div class="left-panel">
					<div class="panel-header">
						<h5 class="mb-2">Questionnaires</h5>
						<b-form-input 
							placeholder="Search questionnaires" 
							class="mb-3" 
							v-model="questionnaireSearch"
							size="sm"
						></b-form-input>
						<small class="text-muted">{{filteredQuestionnaires.length}} Questionnaires</small>
					</div>
					
					<div class="list-container">
						<b-list-group>
							<b-list-group-item 
								v-for="q in filteredQuestionnaires" 
								:key="q.id"
								:active="questionnaire && questionnaire.id === q.id"
								@click="selectQuestionnaire(q)"
								button
								class="questionnaire-item"
							>
								<div class="d-flex align-items-center">
									<i class="fa fa-file-text mr-2"></i>
									<span>{{q.title}}</span>
								</div>
								<small v-if="q.isQuiz" class="text-success d-block">
									<i class="fa fa-check-circle mr-1"></i>Quiz
								</small>
								<small v-if="q.questionnaireQuestions" class="text-muted d-block">
									{{q.questionnaireQuestions.length}} questions
								</small>
							</b-list-group-item>
						</b-list-group>
						
						<div class="mt-3">
							<b-button 
								@click="newQuestionnaire()" 
								variant="success" 
								size="sm" 
								block
							>
								<i class="fa fa-plus mr-1"></i>New Questionnaire
							</b-button>
						</div>
					</div>
				</div>
			</div>

			<!-- Right panel with questionnaire details -->
			<div class="col-md-9" v-if="questionnaire">
				<div class="content-panel">
					<!-- Questionnaire Header -->
					<div class="questionnaire-header mb-4">
						<div class="d-flex justify-content-between align-items-start">
							<div class="questionnaire-title-container">
								<div class="d-flex align-items-center">
									<i class="fa fa-file-text mr-2"></i>
									<h2 class="mb-0">{{questionnaire.title}}</h2>
								</div>
								<div class="mt-2">
									<b-badge v-if="questionnaire.isQuiz" variant="success" class="mr-2">
										<i class="fa fa-check-circle mr-1"></i>Quiz
									</b-badge>
									<b-badge class="mr-2 id-badge">
										ID: {{questionnaire.id || 'New'}}
									</b-badge>
									<b-badge variant="info" v-if="questionnaire.questionnaireQuestions">
										{{questionnaire.questionnaireQuestions.length}} questions
									</b-badge>
								</div>
							</div>
							<div class="action-buttons">
								<b-button 
									variant="outline-primary" 
									size="sm" 
									@click="saveQuestionnaire()"
									class="action-btn"
									title="Save Questionnaire"
								>
									<i class="fa fa-save mr-1"></i>Save
								</b-button>
								<b-button 
									variant="outline-danger" 
									size="sm" 
									@click="deleteQuestionnairePressed()"
									class="action-btn"
									title="Delete Questionnaire"
								>
									<i class="fa fa-trash mr-1"></i>Delete
								</b-button>
							</div>
						</div>
					</div>

					<!-- Questionnaire Form -->
					<div class="form-section mb-4">
						<h4>Questionnaire Details</h4>
						<b-row>
							<b-col md="6">
								<b-form-group label="Title" class="mb-3">
									<b-form-input v-model="questionnaire.title" placeholder="Enter questionnaire title"></b-form-input>
								</b-form-group>
							</b-col>
							<b-col md="6">
								<b-form-group label="Key" class="mb-3">
									<b-form-input v-model="questionnaire.key" placeholder="Use title with no spaces"></b-form-input>
								</b-form-group>
							</b-col>
						</b-row>
						<b-row>
							<b-col md="6">
								<b-form-group label="Tags" class="mb-3">
									<b-form-input v-model="questionnaire.tags" placeholder="Enter tags"></b-form-input>
								</b-form-group>
							</b-col>
							<b-col md="6">
								<b-form-group label="Is Quiz" class="mb-3">
									<b-form-checkbox v-model="questionnaire.isQuiz" class="mt-3"/>
								</b-form-group>
							</b-col>
						</b-row>
						<b-form-group label="Submitted Text" class="mb-3">
							<b-form-textarea 
								v-model="questionnaire.submittedText" 
								rows="3"
								placeholder="Use %SCORE% for quiz score 'x out of y'"
							></b-form-textarea>
						</b-form-group>
						<b-form-group label="Return URL" class="mb-3">
							<b-form-input 
								v-model="questionnaire.returnUrl" 
								placeholder="Enter URL to redirect to after submission"
							></b-form-input>
						</b-form-group>
					</div>

					<!-- Questions Section -->
					<div class="questions-section">
						<div class="d-flex justify-content-between align-items-center mb-3">
							<h4 class="mb-0">Questions ({{questionnaire.questionnaireQuestions ? questionnaire.questionnaireQuestions.length : 0}})</h4>
							<b-button 
								v-if="!addQuestion"
								variant="outline-primary" 
								size="sm"
								@click="addQuestion = true"
								title="Add Question"
							>
								<i class="fa fa-plus mr-1"></i>Add Question
							</b-button>
						</div>

						<!-- Add Question Card -->
						<div v-if="addQuestion" class="add-question-card mb-4">
							<div v-if="!createNewQuestion" class="add-question-options">
								<div class="add-question-header">
									<h5 class="mb-3">
										<i class="fa fa-plus-circle mr-2 text-primary"></i>
										Add a Question to This Questionnaire
									</h5>
									<p class="text-muted mb-4">Choose from existing questions in your library or create a new one.</p>
								</div>
								
								<div class="add-question-content">
									<div class="option-card">
										<div class="option-header">
											<i class="fa fa-list-alt mr-2 text-info"></i>
											<h6 class="mb-2">Select from Library</h6>
											<p class="text-muted small mb-3">Choose an existing question from your question library</p>
										</div>
										<b-form-group label="Select Question:" class="mb-3">
											<div class="modern-dropdown">
												<div class="dropdown-input-wrapper">
													<b-form-input
														v-model="questionSearchTerm"
														placeholder="Type to search questions..."
														@input="filterQuestions"
														@focus="handleFocus"
														@blur="handleBlur"
														class="search-input"
														size="lg"
													></b-form-input>
													<div class="dropdown-arrow">
														<i :class="showDropdown ? 'fa fa-chevron-up' : 'fa fa-chevron-down'"></i>
													</div>
												</div>
												
												<div v-if="showDropdown && filteredQuestions.length > 0" class="dropdown-options">
													<div 
														v-for="question in filteredQuestions" 
														:key="question.questionId"
														class="dropdown-option"
														@mousedown="selectQuestion(question.value)"
													>
														<div class="question-text">{{question.value.text}}</div>
													</div>
												</div>
												
												<div v-if="showDropdown && filteredQuestions.length === 0" class="no-results">
													<div class="no-results-content">
														<i class="fa fa-search text-muted"></i>
														<span>No questions found matching "{{questionSearchTerm}}"</span>
													</div>
												</div>
											</div>
										</b-form-group>
									</div>
									
									<div class="option-divider">
										<span class="divider-text">OR</span>
									</div>
									
									<div class="option-card">
										<div class="option-header">
											<i class="fa fa-plus-circle mr-2 text-success"></i>
											<h6 class="mb-2">Create New Question</h6>
											<p class="text-muted small mb-3">Create a brand new question for this questionnaire</p>
										</div>
										<b-button 
											@click="createQuestionPressed" 
											variant="success" 
											size="lg"
											block
											class="create-question-btn"
										>
											<i class="fa fa-plus mr-2"></i>Create New Question
										</b-button>
									</div>
								</div>
								
								<div class="add-question-footer">
									<b-button 
										@click="addQuestion = false" 
										variant="outline-secondary" 
										size="sm"
									>
										<i class="fa fa-times mr-1"></i>Cancel
									</b-button>
								</div>
							</div>
							<div v-else class="new-question-section">
								<div class="new-question-header">
									<h5 class="mb-3">
										<i class="fa fa-edit mr-2 text-success"></i>
										Create New Question
									</h5>
									<p class="text-muted mb-4">Fill in the details below to create your new question.</p>
								</div>
								<QuestionCard 
									:question="newQuestion" 
									:questionnaireId="questionnaire.id"
									:initEditMode="true"
									:isNewQuestion="true"
									@onChange="onLoad" 
									@onError="showError" 
									@onMessage="showMessage" 
									@cancelNewQuestion="handleCancelNewQuestion"
								/>
							</div>
						</div>

						<!-- Questions List -->
						<div v-if="questionnaire.questionnaireQuestions && questionnaire.questionnaireQuestions.length > 0">
							<div 
								v-for="(qq, index) in questionnaire.questionnaireQuestions" 
								:key="qq.questionId"
								class="question-item"
								draggable
								@dragstart.self="pickupElem($event, qq, index);"
								@dragover.prevent="showDropPlace($event, qq, index);"
								@dragenter.prevent
								@drop="moveElem($event, qq, index);"
								@dragend.prevent="dragEndClear();"
							>
								<div class="question-header" @click="toggleQuestion(qq)">
									<div class="d-flex justify-content-between align-items-center">
										<div class="d-flex align-items-center">
											<!-- Drag Handle Icon -->
											<div class="drag-handle mr-3">
												⋮⋮
											</div>
											
											<i :class="isQuestionExpanded(qq) ? 'fa fa-chevron-down' : 'fa fa-chevron-right'" class="mr-2 toggle-icon"></i>
											<h5 class="mb-0">{{qq.question.text}}</h5>
										</div>
									</div>
								</div>
								
								<div v-show="isQuestionExpanded(qq)" class="question-content mt-3">
									<QuestionCard 
										:question="qq.question" 
										:questionnaireId="qq.questionnaireId"
										@onChange="onLoad"
										@onError="showError" 
										@onMessage="showMessage"  
									/>
								</div>
							</div>
						</div>
						<div v-else class="no-questions">
							<p class="text-muted text-center">No questions added yet. Click "Add Question" to get started.</p>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Questions Tab -->
		<div v-if="!isLoading && !error && tabIndex === 1" class="questions-tab">
			<div class="content-panel">
				<div class="d-flex justify-content-between align-items-center mb-4">
					<div>
						<h2>Question Library</h2>
						<p class="text-muted mb-0">{{filteredLibraryQuestions.length}} of {{questions ? questions.length : 0}} questions</p>
					</div>
					<b-button variant="success" @click="createQuestionPressed">
						<i class="fa fa-plus mr-1"></i>Create New Question
					</b-button>
				</div>

				<!-- Search Filter -->
				<div class="search-filter mb-4">
					<b-form-input 
						v-model="questionLibrarySearch" 
						placeholder="Search questions by text..." 
						size="lg"
						class="search-input"
					>
						<template #prepend>
							<i class="fa fa-search"></i>
						</template>
					</b-form-input>
				</div>

				<!-- New Question Form -->
				<div v-if="createNewQuestion" class="new-question-card mb-4">
					<h4>New Question:</h4>
					<QuestionCard 
						:question="newQuestion" 
						:initEditMode="true"
						:isNewQuestion="true"
						@cancelNewQuestion="handleCancelNewQuestion"
						@onCreateNew="handleCreateNewQuestion" 
						@onError="showError" 
						@onMessage="showMessage" 
					/>
				</div>

				<!-- Questions Grid -->
				<div v-if="filteredLibraryQuestions && filteredLibraryQuestions.length > 0" class="questions-grid">
					<div v-for="q in filteredLibraryQuestions" :key="q.questionId" class="question-card">
						<div class="question-header" @click="toggleLibraryQuestion(q)">
							<div class="d-flex justify-content-between align-items-center">
								<div class="d-flex align-items-center">
									<b-button 
										variant="outline-info" 
										size="sm" 
										@click.stop.prevent="showQuestionnairesUsingQuestion(q)"
										class="mr-3"
										title="Show Questionnaires Using This Question"
									>
										<i class="fa fa-list mr-1"></i>Usage ({{getQuestionUsageCount(q.questionId)}})
									</b-button>
									<i :class="isLibraryQuestionExpanded(q) ? 'fa fa-chevron-down' : 'fa fa-chevron-right'" class="mr-2 toggle-icon"></i>
									<h5 class="mb-0">{{q.value.text}}</h5>
								</div>
							</div>
						</div>
						
						<div v-show="isLibraryQuestionExpanded(q)" class="question-content mt-3">
							<QuestionCard 
								:question="q.value" 
								@onChange="onLoad" 
								@onError="showError" 
								@onMessage="showMessage" 
							/>
						</div>
					</div>
				</div>
				<div v-else class="no-questions">
					<p class="text-muted text-center">No questions in the library yet. Create your first question to get started.</p>
				</div>
			</div>
		</div>
	</div>

	<!-- Modals -->
	<b-modal id="modMessage" hide-backdrop content-class="shadow" header-bg-variant="dark" header-text-variant="light" title="CMS Admin">
		<p class="my-4">{{message}}</p>
	</b-modal>
	
	<b-modal id="modSure" hide-backdrop content-class="shadow" header-bg-variant="dark" header-text-variant="light" title="Really?">
		<p class="my-4">Are you sure?</p>
		<p class="my-4">{{deleteWarningMessage}}</p>
		<template #modal-footer="{ cancel }">
			<b-button variant="success" @click="delMethod()">Yes</b-button>
			<b-button variant="danger" @click="cancel()">No</b-button>
		</template>
	</b-modal>

	<b-modal 
		id="questionUsageModal" 
		hide-backdrop 
		content-class="shadow" 
		header-bg-variant="info" 
		header-text-variant="light" 
		title="Questionnaires Using This Question"
		size="lg"
		scrollable
		no-fade
	>
		<div v-if="selectedQuestionForUsage">
			<h6 class="mb-3">{{selectedQuestionForUsage.value.text}}</h6>
			<div v-if="questionnairesUsingQuestion.length > 0">
				<p class="text-muted mb-3">This question is used in {{questionnairesUsingQuestion.length}} questionnaire(s):</p>
				<b-list-group>
					<b-list-group-item 
						v-for="questionnaire in questionnairesUsingQuestion" 
						:key="questionnaire.id"
						class="d-flex justify-content-between align-items-center"
					>
						<div>
							<strong>{{questionnaire.title}}</strong>
							<div class="text-muted small">
								ID: {{questionnaire.id}} | 
								<span v-if="questionnaire.isQuiz" class="text-success">
									<i class="fa fa-check-circle mr-1"></i>Quiz
								</span>
								<span v-else class="text-info">
									<i class="fa fa-file-text mr-1"></i>Questionnaire
								</span>
							</div>
						</div>
						<b-button 
							variant="outline-primary" 
							size="sm"
							@click="selectQuestionnaireFromUsage(questionnaire)"
						>
							<i class="fa fa-external-link mr-1"></i>View
						</b-button>
					</b-list-group-item>
				</b-list-group>
			</div>
			<div v-else class="text-center py-4">
				<i class="fa fa-info-circle text-muted mb-2" style="font-size: 2rem;"></i>
				<p class="text-muted">This question is not used in any questionnaires yet.</p>
			</div>
		</div>
		<template #modal-footer="{ ok }">
			<b-button variant="secondary" @click="ok()">Close</b-button>
		</template>
	</b-modal>
</div>
</template>

<script>
import QuestionCard from "./Widgets/QuestionCardV2";

export default {
	name: 'Questions',
	components: {
		QuestionCard
	},
	data() {
		return {
			tabIndex: 0,
			questionnaires: null,
			questionnaire: null,
			questions: null,
			addQuestion: false,
			createNewQuestion: false,
			delMethod: null,
			dragedElem: null,
			overElem: null,
			message: null,
			deleteWarningMessage: null,
			error: null,
			isLoading: false,
			questionnaireSearch: '',
			selectedQuestionFromLibrary: null,
			expandedQuestionId: null,
			expandedLibraryQuestionId: null,
			isNewQuestion: false,
			showAlert: false,
			alertVariant: 'success',
			alertMessage: '',
			selectedQuestionForUsage: null,
			questionnairesUsingQuestion: [],
			questionSearchTerm: '',
			showDropdown: false,
			filteredQuestions: [],
			questionLibrarySearch: ''
		}
	},
	computed: {
		newQuestion() {
			return {
				id: "",
				text: "",
				questionType: 0,
				possibleAnswers: []
			}
		},
		filteredQuestionnaires() {
			if (!this.questionnaires) return [];
			if (!this.questionnaireSearch) return this.questionnaires;
			
			const searchLower = this.questionnaireSearch.toLowerCase();
			return this.questionnaires.filter(q => 
				q.title.toLowerCase().includes(searchLower) ||
				(q.tags && q.tags.toLowerCase().includes(searchLower))
			);
		},
		filteredLibraryQuestions() {
			if (!this.questions) return [];
			if (!this.questionLibrarySearch) return this.questions;
			
			const searchLower = this.questionLibrarySearch.toLowerCase();
			return this.questions.filter(q => 
				q.value.text.toLowerCase().includes(searchLower) ||
				(q.value.key && q.value.key.toLowerCase().includes(searchLower))
			);
		},
		questionOptions() {
			if (!this.questions) return [];
			return this.questions.map(q => ({
				value: q.value,
				text: q.value.text
			}));
		},
		questionTypeOptions() {
			return [
				{ value: 0, text: 'Multiple Choice' },
				{ value: 1, text: 'Single Choice' },
				{ value: 2, text: 'Text Area' }
			];
		},
		getQuestionUsageCount() {
			return (questionId) => {
				if (!this.questionnaires) return 0;
				return this.questionnaires.filter(questionnaire => {
					if (!questionnaire.questionnaireQuestions) return false;
					return questionnaire.questionnaireQuestions.some(qq => qq.questionId === questionId);
				}).length;
			};
		}
	},
	methods: {
		getQuestionnaires: function(){
			this.isLoading = true;
			this.$http.get(this.$config.ApiUri + "/Questionnaires")
				.then((result) => {
					this.questionnaires = result.data;
					if (this.questionnaires.length > 0) {
						if (this.questionnaire != null) {
							this.questionnaire = this.questionnaires.find(q => q.id == this.questionnaire.id);
						} else {
							this.questionnaire = this.questionnaires[0];
						}
					}
				})
				.catch((error) => {
					this.error = 'Failed to load questionnaires';
					console.error('Error loading questionnaires:', error);
				})
				.finally(() => {
					this.isLoading = false;
				});
		},
		selectQuestionnaire: function(p) {
			this.addQuestion = false;
			this.createNewQuestion = false;
			this.questionnaire = p;
		},
		newQuestionnaire() {
			this.questionnaire = { 
				"id": "",
				"key": "",
				"title": "",
				"isQuiz": false,
				"submittedText": "",
				"questionnaireQuestions": []
			};
		},
		saveQuestionnaire: function(){
			if (this.questionnaire.id == null || this.questionnaire.id == "") {
				delete this.questionnaire.id;
				this.$http.post(this.$config.ApiUri + "/Questionnaires", this.questionnaire)
					.then((result) => {
						this.questionnaire = result.data;
						this.getQuestionnaires(false);
						this.showMessage("Questionnaire created successfully!");
					}
				).catch(() => { this.showError(); });
			}
			else {
				this.$http.put(this.$config.ApiUri + "/Questionnaires/" + this.questionnaire.id, this.questionnaire)
					.then((result) => {
						this.questionnaire = result.data;
						this.getQuestionnaires(false);
						this.showMessage("Questionnaire saved successfully!");
					}
				).catch(() => { this.showError(); });
			} 
			this.createNewQuestion = false;
		},
		deleteQuestionnairePressed() {
			this.deleteWarningMessage = "Deleting a questionnaire";
			this.delMethod = this.deleteQuestionnaire;
			this.$bvModal.show('modSure');
		},
		deleteQuestionnaire() {
			this.$bvModal.hide('modSure');
			this.$http.delete(this.$config.ApiUri + "/Questionnaires/" + this.questionnaire.id)
				.then(() => {
					this.questionnaire = null;
					this.getQuestionnaires(true);
					this.showMessage("Questionnaire deleted successfully!");
				}
			).catch((error) => { this.showError("Sorry, that Questionnaire cannot be deleted. (" + error + ")"); });
		},
		getQuestions: function(){
			this.isLoading = true;
			this.$http.get(this.$config.ApiUri + "/Questions")
				.then((result) => {
					this.questions = [];
					result.data.forEach((item) => {
						this.questions.push({
							questionId: item.id,
							value: item,
							text: item.text
						});
					});
				})
				.catch((error) => {
					this.error = 'Failed to load questions';
					console.error('Error loading questions:', error);
				})
				.finally(() => {
					this.isLoading = false;
				});
		},
		createQuestionPressed: function() {
			this.createNewQuestion = true;
		},
		handleCancelNewQuestion: function() {
			this.createNewQuestion = false;
			this.showQuestionModal = false;
			this.editingQuestion = null;
		},
		handleCreateNewQuestion: function() {
			this.createNewQuestion = false;
			this.showQuestionModal = false;
			this.editingQuestion = null;
			this.getQuestions();
			if (this.tabIndex == 0) {
				this.getQuestionnaires();
			}
			this.$forceUpdate();
		},
		addQuestionToQuestionnaire: function(item) {
			console.log("add item to questionnaire")
			console.log(item);
			if (this.questionnaire.id != null && this.questionnaire.id != "") {
				if (item.id != null && item.id != "") {
					this.$http.post(this.$config.ApiUri + "/Questionnaires/" + this.questionnaire.id + "/QuestionnaireQuestions/" + item.id)
						.then(() => {
							this.showMessage("Question added to questionnaire successfully!");
							this.addQuestion = false;
							this.createNewQuestion = false;
							this.onLoad();
						}
					).catch((e) => { 
						console.log("Error adding question from questionnaire ");
						console.log(e);
						this.showError("Failed to add question to questionnaire");
					});
				}
			} 
		},
		loadQuestionnaireTab() {
			this.tabIndex = 0;
			this.addQuestion = false;
			this.createNewQuestion = false;
			this.getQuestionnaires(true);
		},
		loadQuestionTab() {
			this.tabIndex = 1;
			this.addQuestion = false;
			this.createNewQuestion = false;
			this.getQuestions();
		},
		dragEndClear() {
			console.log("dragEnd");
			this.dragedElem = null;
			this.overElem = null;
		},
		pickupElem(event, elem) {
			console.log("pick up elem");
			console.log(elem);
			event.dataTransfer.dropEffect = "move";
			this.dragedElem = { ...elem };
		},
		showDropPlace(event, elem) {
			if (elem.id !== this.dragedElem.id) {
				this.overElem = { ...elem };
			} else {
				this.overElem = null;
			}
		},
		moveElem(event, elem, index) {
			console.log(
				`moveElem: event: ${event} | elem: ${elem} | index: ${index}`
			);
			this.questionnaire.questionnaireQuestions = this.questionnaire.questionnaireQuestions.filter(item => item.questionId !== this.dragedElem.questionId);
			this.questionnaire.questionnaireQuestions.splice(index, 0, { ...this.dragedElem });
			this.questionnaire.questionnaireQuestions.forEach((item, index) => (item.order = index + 1));
		},
		showMessage(message, variant = 'success') {
			this.alertMessage = message;
			this.alertVariant = variant;
			this.showAlert = true;
			setTimeout(() => {
				this.showAlert = false;
			}, 3000);
		},
		showError(message) {
			this.showMessage(message || 'An error occurred', 'danger');
		},
		onLoad() {
			this.createNewQuestion = false;
			this.addQuestion = false;
			this.getQuestionnaires(true);
			this.getQuestions();
			this.$forceUpdate();
		},
		toggleQuestion(question) {
			console.log('=== TOGGLE QUESTION ===');
			console.log('Clicked question ID:', question.questionId);
			console.log('Current expandedQuestionId:', this.expandedQuestionId);
			console.log('Will set to:', this.expandedQuestionId === question.questionId ? null : question.questionId);
			
			this.expandedQuestionId = this.expandedQuestionId === question.questionId ? null : question.questionId;
			
			console.log('New expandedQuestionId:', this.expandedQuestionId);
			console.log('======================');
		},
		isQuestionExpanded(question) {
			const result = this.expandedQuestionId === question.questionId;
			console.log('isQuestionExpanded for', question.questionId, 'returns:', result, '(expandedQuestionId is:', this.expandedQuestionId, ')');
			return result;
		},
		toggleLibraryQuestion(question) {
			this.expandedLibraryQuestionId = this.expandedLibraryQuestionId === question.questionId ? null : question.questionId;
		},
		isLibraryQuestionExpanded(question) {
			return this.expandedLibraryQuestionId === question.questionId;
		},
		showQuestionnairesUsingQuestion(question) {
			// Store current scroll position
			const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
			
			// Prevent any scroll changes
			const preventScroll = () => {
				window.scrollTo(0, scrollPosition);
			};
			
			// Add scroll prevention temporarily
			window.addEventListener('scroll', preventScroll);
			
			this.selectedQuestionForUsage = question;
			this.getQuestionnairesUsingQuestion(question);
			
			// Remove scroll prevention after modal is fully opened
			setTimeout(() => {
				window.removeEventListener('scroll', preventScroll);
				window.scrollTo(0, scrollPosition);
			}, 200);
		},
		getQuestionnairesUsingQuestion(question) {
			this.isLoading = true;
			// Try the API endpoint first, fallback to local search
			this.$http.get(this.$config.ApiUri + "/Questionnaires/UsingQuestion/" + question.questionId)
				.then((result) => {
					this.questionnairesUsingQuestion = result.data;
					this.$bvModal.show('questionUsageModal');
				})
				.catch(() => {
					console.log('API endpoint not available, searching locally...');
					// Fallback: search through existing questionnaires
					this.searchQuestionnairesLocally(question);
				})
				.finally(() => {
					this.isLoading = false;
				});
		},
		searchQuestionnairesLocally(question) {
			if (!this.questionnaires) {
				this.questionnairesUsingQuestion = [];
				this.$bvModal.show('questionUsageModal');
				return;
			}
			
			const usingQuestionnaires = this.questionnaires.filter(questionnaire => {
				if (!questionnaire.questionnaireQuestions) return false;
				return questionnaire.questionnaireQuestions.some(qq => qq.questionId === question.questionId);
			});
			
			this.questionnairesUsingQuestion = usingQuestionnaires;
			this.$bvModal.show('questionUsageModal');
		},
		selectQuestionnaireFromUsage(questionnaire) {
			// Switch to questionnaire tab first
			this.tabIndex = 0;
			// Select the questionnaire
			this.selectQuestionnaire(questionnaire);
			// Close the modal
			this.$bvModal.hide('questionUsageModal');
		},
		filterQuestions() {
			if (!this.questions) {
				this.filteredQuestions = [];
				return;
			}
			
			if (!this.questionSearchTerm || this.questionSearchTerm.trim() === '') {
				// Show all questions when search term is empty
				this.filteredQuestions = this.questions;
			} else {
				// Filter questions based on search term
				this.filteredQuestions = this.questions.filter(q => 
					q.value.text.toLowerCase().includes(this.questionSearchTerm.toLowerCase())
				);
			}
		},
		selectQuestion(question) {
			this.selectedQuestionFromLibrary = question;
			this.questionSearchTerm = question.text;
			this.showDropdown = false;
			this.addQuestionToQuestionnaire(question);
		},
		handleBlur() {
			// Delay hiding dropdown to allow for option selection
			setTimeout(() => {
				this.showDropdown = false;
			}, 200);
		},
		handleFocus() {
			this.showDropdown = true;
			// Initialize with all questions when dropdown opens
			this.filterQuestions();
		},
		getQuestionTypeText(questionType) {
			switch (questionType) {
				case 0:
					return 'Multiple Choice';
				case 1:
					return 'Single Choice';
				case 2:
					return 'Text Area';
				default:
					return 'Unknown';
			}
		},
		navigateToV1() {
			// Navigate back to the original Questions component
			this.$router.push('/questions');
		}
	},
	mounted: function() { 
		this.onLoad(); 
	}
}
</script>

<style scoped>
.submenu {
	background-color: #f8f9fa;
	border-bottom: 1px solid #dee2e6;
	height: 40px;
	font-size: 14px;
}

.submenu a {
	color: #495057;
	text-decoration: none;
	margin-right: 20px;
	padding: 8px 12px;
	border-radius: 4px;
	transition: all 0.2s;
}

.submenu a:hover {
	color: #007bff;
	background-color: #e9ecef;
}

.submenu a.active {
	color: #007bff;
	background-color: #e3f2fd;
	font-weight: 500;
}

.alert-fixed {
	position: fixed;
	top: 56px;
	left: 50%;
	transform: translateX(-50%);
	z-index: 1000;
	min-width: 300px;
	text-align: center;
}

.loading-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 40px;
}

.left-panel {
	background-color: white;
	border: 1px solid #dee2e6;
	border-radius: 8px;
	overflow: hidden;
	box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.panel-header {
	padding: 16px;
	border-bottom: 1px solid #dee2e6;
	background-color: #f8f9fa;
}

.list-container {
	max-height: calc(100vh - 300px);
	overflow-y: auto;
	padding: 16px;
}

.questionnaire-item {
	cursor: pointer;
	padding: 12px 16px;
	border: 1px solid #dee2e6;
	border-radius: 6px;
	margin-bottom: 8px;
	transition: all 0.2s;
}

.questionnaire-item:hover {
	background-color: #f8f9fa;
	border-color: #adb5bd;
}

.questionnaire-item.active,
.questionnaire-item.active:hover {
	background-color: #e3f2fd !important;
	border-color: #1976d2 !important;
	color: #1976d2 !important;
}

.content-panel {
	padding: 24px;
	background-color: white;
	border-radius: 8px;
	box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.questionnaire-header {
	padding-bottom: 20px;
	border-bottom: 1px solid #dee2e6;
}

.questionnaire-title-container {
	flex: 1;
	margin-right: 1rem;
}

.action-buttons {
	white-space: nowrap;
}

.action-buttons .action-btn {
	margin-right: 8px;
}

.action-buttons .action-btn:last-child {
	margin-right: 0;
}

.form-section {
	background-color: #f8f9fa;
	padding: 20px;
	border-radius: 6px;
	border: 1px solid #dee2e6;
}

.questions-section {
	margin-top: 24px;
}

.add-question-card {
	background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
	padding: 24px;
	border-radius: 12px;
	border: 1px solid #dee2e6;
	box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.add-question-header {
	text-align: center;
	margin-bottom: 24px;
}

.add-question-header h5 {
	color: #495057;
	font-weight: 600;
}

.add-question-content {
	display: flex;
	align-items: stretch;
	gap: 20px;
	margin-bottom: 24px;
	flex-wrap: wrap;
}

.option-card {
	flex: 1;
	min-width: 280px;
	max-width: 400px;
	background: white;
	padding: 20px;
	border-radius: 8px;
	border: 1px solid #dee2e6;
	box-shadow: 0 2px 4px rgba(0,0,0,0.05);
	transition: all 0.3s ease;
	text-align: center;
}

.option-card:hover {
	box-shadow: 0 4px 8px rgba(0,0,0,0.1);
	transform: translateY(-2px);
}

.option-header {
	text-align: center;
	margin-bottom: 16px;
}

.option-header h6 {
	color: #495057;
	font-weight: 600;
	margin-bottom: 8px;
}

.option-header p {
	font-size: 0.875rem;
	line-height: 1.4;
}

.option-divider {
	display: flex;
	align-items: center;
	justify-content: center;
	position: relative;
	margin: 0 10px;
	min-width: 60px;
	flex-shrink: 0;
}

.divider-text {
	background: white;
	padding: 8px 16px;
	border-radius: 20px;
	border: 2px solid #dee2e6;
	color: #6c757d;
	font-weight: 600;
	font-size: 0.875rem;
	z-index: 1;
	white-space: nowrap;
}

.add-question-footer {
	text-align: center;
	padding-top: 16px;
	border-top: 1px solid #dee2e6;
}

.new-question-section {
	background: white;
	padding: 20px;
	border-radius: 8px;
	border: 1px solid #dee2e6;
}

.new-question-header {
	text-align: center;
	margin-bottom: 20px;
}

.new-question-header h5 {
	color: #495057;
	font-weight: 600;
}

.question-select {
	border-radius: 6px;
	max-width: 100%;
}

.create-question-btn {
	border-radius: 6px;
	font-weight: 500;
	transition: all 0.3s ease;
}

.create-question-btn:hover {
	transform: translateY(-1px);
	box-shadow: 0 4px 8px rgba(0,0,0,0.15);
}

/* Responsive design for mobile and smaller screens */
@media (max-width: 992px) {
	.add-question-content {
		flex-direction: column;
		gap: 16px;
	}
	
	.option-card {
		min-width: auto;
		flex: none;
	}
	
	.option-divider {
		margin: 10px 0;
		order: -1;
	}
	
	.add-question-card {
		padding: 16px;
	}
}

@media (max-width: 768px) {
	.add-question-card {
		padding: 16px;
	}
	
	.option-card {
		padding: 16px;
	}
}

.question-item {
	margin-bottom: 16px;
	cursor: move;
	border: 1px solid #dee2e6;
	border-radius: 8px;
	overflow: hidden;
	transition: all 0.2s;
	background-color: white;
	box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.question-item:hover {
	transform: translateY(-1px);
	box-shadow: 0 4px 8px rgba(0,0,0,0.15);
}

.drag-handle {
	color: #6c757d;
	cursor: grab;
	padding: 4px 8px;
	border-radius: 4px;
	transition: all 0.2s ease;
	user-select: none;
	font-size: 14px;
	font-weight: bold;
	background-color: #f8f9fa;
	border: 1px solid #dee2e6;
}

.drag-handle:hover {
	color: #007bff;
	background-color: #e9ecef;
}

.drag-handle:active {
	cursor: grabbing;
}

.no-questions {
	padding: 40px;
	text-align: center;
	background-color: #f8f9fa;
	border-radius: 6px;
	border: 2px dashed #dee2e6;
}

.questions-tab {
	padding: 24px;
}

.questions-grid {
	display: flex;
	flex-direction: column;
	gap: 5px;
}

.question-card {
	background-color: white;
	border: 1px solid #dee2e6;
	border-radius: 8px;
	overflow: hidden;
	transition: all 0.2s;
	margin-bottom: 16px;
	box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.question-card:hover {
	box-shadow: 0 4px 8px rgba(0,0,0,0.15);
}

.new-question-card {
	background-color: #e8f5e8;
	padding: 20px;
	border-radius: 6px;
	border: 1px solid #c8e6c9;
}

/* Animation for drag and drop */
.question-item {
	transition: all 0.3s ease;
}

.question-item--is-dragged {
	opacity: 0.7;
	transform: rotate(2deg);
}

.drop-place {
	position: relative;
	transition: all 0.3s ease;
}

.drop-place--before {
	padding-top: 40px;
}

.drop-place--after {
	padding-bottom: 40px;
}

.drop-place--before:before {
	position: absolute;
	top: 5px;
	left: 0;
	text-align: center;
	content: "Drop here";
	width: 100%;
	padding: 5px 0;
	opacity: 0.7;
	border: 1px dashed #007bff;
	box-shadow: inset 0px 0px 5px 2px #007bff;
}

.drop-place--after:after {
	position: absolute;
	bottom: 5px;
	left: 0;
	text-align: center;
	content: "Drop here";
	width: 100%;
	padding: 5px 0;
	opacity: 0.7;
	border: 1px dashed #007bff;
	box-shadow: inset 0px 0px 5px 2px #007bff;
}

.question-header {
	cursor: pointer;
	padding: 16px 20px;
	background-color: #f8f9fa;
	border-bottom: 1px solid #dee2e6;
	box-sizing: border-box;
	min-height: 60px;
	display: flex;
	align-items: center;
	transition: all 0.2s;
}

.question-header:hover {
	background-color: #e9ecef;
}

.question-header h5 {
	margin: 0;
	font-size: 1.1rem;
	font-weight: 500;
	color: #212529;
}

.toggle-icon {
	font-size: 0.875rem;
	color: #6c757d;
	width: 16px;
	text-align: center;
	transition: transform 0.2s;
}

.question-actions {
	white-space: nowrap;
	margin-left: 0.5rem;
}

.edit-button {
	padding: 0.25rem 0.5rem;
	font-size: 1rem;
	border-width: 1px;
	background-color: transparent;
	min-width: 32px;
	height: 32px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	line-height: 1;
}

.edit-button:hover {
	background-color: #f8f9fa;
	border-color: #6c757d;
}

.question-content {
	padding: 20px;
	background-color: white;
}

.possible-answers-section {
	background-color: #f8f9fa;
	padding: 16px;
	border-radius: 6px;
	border: 1px solid #dee2e6;
}

.answer-item {
	background-color: white;
	padding: 12px;
	border-radius: 4px;
	border: 1px solid #dee2e6;
}

.no-answers {
	padding: 20px;
	text-align: center;
	background-color: white;
	border-radius: 4px;
	border: 2px dashed #dee2e6;
}

.text-area-settings {
	background-color: #f8f9fa;
	padding: 16px;
	border-radius: 6px;
	border: 1px solid #dee2e6;
}

.additional-settings-section {
	background-color: #f8f9fa;
	padding: 16px;
	border-radius: 6px;
	border: 1px solid #dee2e6;
	margin-top: 16px;
}

.question-edit-form {
	padding: 16px 0;
}

/* Prevent modal from affecting page scroll */
.modal-open {
	overflow: auto !important;
}

.modal {
	overflow-y: auto;
}

.modern-dropdown {
	position: relative;
}

.dropdown-input-wrapper {
	position: relative;
	display: flex;
	align-items: center;
	border: 1px solid #dee2e6;
	border-radius: 6px;
	background-color: white;
	transition: border-color 0.2s;
}

.dropdown-input-wrapper:focus-within {
	border-color: #007bff;
	box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.search-input {
	border: none;
	box-shadow: none;
	flex: 1;
}

.search-input:focus {
	box-shadow: none;
}

.dropdown-arrow {
	padding: 0 12px;
	color: #6c757d;
	pointer-events: none;
}

.dropdown-options {
	position: absolute;
	top: 100%;
	left: 0;
	right: 0;
	background-color: white;
	border: 1px solid #dee2e6;
	border-top: none;
	border-radius: 0 0 6px 6px;
	box-shadow: 0 4px 12px rgba(0,0,0,0.15);
	max-height: 300px;
	overflow-y: auto;
	z-index: 1000;
}

.dropdown-option {
	padding: 12px 16px;
	cursor: pointer;
	transition: all 0.2s;
	border-bottom: 1px solid #f8f9fa;
}

.dropdown-option:last-child {
	border-bottom: none;
}

.dropdown-option:hover {
	background-color: #f8f9fa;
}

.dropdown-option:active {
	background-color: #e9ecef;
}

.question-text {
	font-weight: 500;
	color: #212529;
	line-height: 1.4;
	text-align: left;
}

.no-results {
	position: absolute;
	top: 100%;
	left: 0;
	right: 0;
	background-color: white;
	border: 1px solid #dee2e6;
	border-top: none;
	border-radius: 0 0 6px 6px;
	box-shadow: 0 4px 12px rgba(0,0,0,0.15);
	z-index: 1000;
}

.no-results-content {
	padding: 16px;
	text-align: center;
	color: #6c757d;
	font-style: italic;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
}

/* Responsive design for mobile */
@media (max-width: 768px) {
	.dropdown-options {
		max-height: 250px;
	}
}

/* Search Filter Styling */
.search-filter {
	background-color: #f8f9fa;
	padding: 16px;
	border-radius: 8px;
	border: 1px solid #dee2e6;
}

.search-input {
	border-radius: 6px;
}

.search-input .form-control {
	border: 1px solid #dee2e6;
	transition: border-color 0.2s, box-shadow 0.2s;
}

.search-input .form-control:focus {
	border-color: #007bff;
	box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.search-input .input-group-prepend .input-group-text {
	background-color: #f8f9fa;
	border: 1px solid #dee2e6;
	border-right: none;
	color: #6c757d;
}

.search-input .form-control {
	border-left: none;
}

.search-input .form-control:focus + .input-group-append .input-group-text,
.search-input .form-control:focus ~ .input-group-prepend .input-group-text {
	border-color: #007bff;
}

/* ID Badge Styling */
.id-badge {
	background-color: #5e6063 !important;
	color: white !important;
	font-weight: 500 !important;
	border: 1px solid #393a3b !important;
	padding: 6px 12px !important;
	font-size: 0.875rem !important;
}
</style>